/* The census. Colour is never the only signal: every state carries a word, because a reader who
   cannot distinguish two hues must still be able to tell "missing" from "declared nil" — and
   because a screenshot in a review pack loses the legend. */

/* The verdict is small print that still says what it means: a gap is absent, not zero. The first
   sentence is `--warn` because it genuinely needs attention; the rest is muted and capped. */
.verdict {
  margin: 10px 0 0;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
  overflow-wrap: anywhere;
}

.verdictGap {
  color: var(--warn);
  font-weight: 600;
}

/* Padding for the parts of the register's panel that are prose and forms: the panel is flush so
   the table can run edge to edge, which leaves the sentences around it to carry their own. */
.pad {
  padding: 10px 14px;
}

.pad > * + * {
  margin-top: 8px;
}

/* The register's columns, shared by its header and every row so they cannot disagree. */
.cols {
  --cols: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1.3fr) auto;
}

/* One stream. The first four items are the table's cells and sit on one line; everything after
   them - the life-end sentence, the twelve periods, a form, a trail - takes the full width
   beneath, so an open form is under the row it belongs to and never beside it. */
.sourceRow > :not(.rowHead, .owner, .supply, .rowActions) {
  grid-column: 1 / -1;
}

.rowHead {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.rowName {
  font-size: var(--t-dense);
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.rowMeta {
  font-size: var(--t-min);
  color: var(--muted);
}

/* The row's own controls, in the last column of the first line. */
.rowActions {
  grid-column: 4;
  grid-row: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px 14px;
}

/* The second button style: a way out that is not the act. */
.ghost {
  padding: 6px 11px;
  border: 1px solid var(--line-2);
  border-radius: 6px;
  background: var(--panel);
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 500;
  color: var(--ink-2);
  cursor: pointer;
}

.ghost:hover {
  border-color: var(--accent);
  color: var(--accent);
}

/* One cell per window. A strip rather than a table: twelve months has to read at a glance, and the
   question a reader asks is "where are the gaps", not "what is the value of March". */
.strip {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--t-min);
  font-weight: 600;
  max-width: 100%;
  white-space: normal;
  text-align: left;
  overflow-wrap: anywhere;
  font-variant-numeric: lining-nums tabular-nums;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: none;
  cursor: default;
}

/* Arrived is the unremarkable state, so it is the quietest: twelve green chips in a row would
   outshout the one amber one, and the reader is here to find the gap. */
.submitted {
  color: var(--ink-2);
  background: none;
}

.declared_nil {
  color: var(--ink-2);
  background: var(--panel-2);
  border-color: var(--line);
}

.missing {
  color: var(--warn);
  background: var(--warn-w);
  border-color: var(--warn);
}

.pending {
  color: var(--ink-2);
  background: var(--panel-2);
  border-style: dashed;
  border-color: var(--line);
}

.unknown {
  color: var(--ink-2);
  background: var(--panel-2);
  border-color: var(--line-2);
}

.not_expected {
  color: var(--muted);
  background: none;
  border-color: var(--line-2);
}

.actionable {
  cursor: pointer;
}

.actionable:hover {
  filter: brightness(1.1);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--line);
  font-size: var(--t-min);
  color: var(--muted);
  line-height: 1.5;
}

.legendItem {
  display: flex;
  align-items: center;
  gap: 5px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 8px;
  padding: 9px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--panel-2);
}

.formHead {
  margin: 0;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink);
}

.formNote {
  margin: 0;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.input {
  font: inherit;
  font-size: var(--t-min);
  padding: 5px 7px;
  border: 1px solid var(--line);
  border-radius: 5px;
  background: var(--panel);
  color: var(--ink);
}

.buttons {
  display: flex;
  gap: 7px;
}

.error {
  margin: 6px 0 0;
  font-size: var(--t-min);
  color: var(--warn);
}

/* **EXP-06.** What arrived behind one expected source, expanded on request. Prose and links
   rather than a nested table: this is a trail to follow, not a second register to read. */
.expand {
  margin: 8px 0 0;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}

.links {
  margin: 6px 0 0;
  padding: 10px 12px;
  border-left: 2px solid var(--line-2);
  background: var(--panel-2);
}

.linkList {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 4px;
}

.linkItem {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.link {
  color: var(--accent);
}

/* Said, never implied by absence: a replaced figure that looks live is one nobody re-checks. */
.superseded {
  color: var(--warn);
}

/* **B-23.** Declaring a stream the census should expect, in the panel where its absence is read. */
.declareOpen {
  margin: 14px 0 0;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}

.declare {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  align-items: start;
  margin: 14px 0 0;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: var(--panel);
}

.field {
  display: grid;
  gap: 4px;
}

.fieldLabel {
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink);
}

.declareActions {
  display: flex;
  gap: 8px;
  align-self: end;
}

.declareSubmit {
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 600;
  padding: 8px 14px;
  border: 0;
  border-radius: 6px;
  background: var(--accent);
  color: var(--on-fill);
  cursor: pointer;
}

.declareSubmit:disabled {
  border-color: var(--btn-off-bg);
  background: var(--btn-off-bg);
  color: var(--btn-off-ink);
  cursor: default;
}

.declareCancel {
  font: inherit;
  font-size: var(--t-dense);
  padding: 8px 12px;
  border: 1px solid var(--line-2);
  border-radius: 6px;
  background: var(--panel);
  color: var(--ink-2);
  cursor: pointer;
}

/* **Full width.** The server's refusals name what to do instead — "Published: natural_gas, …" —
   and in a grid cell that sentence renders a word at a time. */
.declareError {
  grid-column: 1 / -1;
  margin: 0;
  padding: 9px 12px;
  border: 1px solid var(--crit-line);
  border-radius: var(--radius);
  background: var(--crit-w);
  color: var(--crit);
  font-size: var(--t-dense);
  line-height: 1.55;
}

/* The derived end of a source's life (CTX-03). Its own line rather than more text in `.rowMeta`:
   it is the explanation for a run of excused windows directly beneath it, and a reader scanning
   for gaps has to be able to see which rows have stopped rather than parse a meta line for a date.
   `--warn` because it is a fact to know and not a fault — the same reading as `.superseded`. */
.lifeEnd {
  margin: 0;
  max-width: 92ch;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--muted);
}

/* **COM-02.** Who is accountable for this stream arriving, and the control that changes it.

   On the row rather than in a panel of its own: the person reading a gap is the person who knows
   whose gap it is, and an owner is a standing fact about the supply — which is why the summary
   counts it over sources rather than over windows. */
.owner {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  font-size: var(--t-min);
}

/* The column header names this on a wide screen, so the label is for a screen reader there and is
   shown only when the columns collapse and the header goes. Visually hidden rather than removed:
   `display: none` would take it out of the accessibility tree and leave a name with no noun. */
.ownerLabel {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

@media (max-width: 900px) {
  .ownerLabel {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    overflow: visible;
    clip-path: none;
    color: var(--muted);
  }

  .rowActions {
    grid-column: 1;
    grid-row: auto;
    justify-content: flex-start;
  }
}

.ownerValue {
  font-weight: 600;
  color: var(--ink);
}

/* `--warn`, the same reading the summary's "No owner" figure gets: something to resolve, not a
   fault — and never a colour on its own, which is why the word is "Nobody" and not an empty slot. */
.ownerNobody {
  font-weight: 600;
  color: var(--warn);
}

.ownerPick {
  /* A select takes the width of its longest option, which on a phone is wider than the screen (the F6
     sweep measured the source page's body 50px too wide). It may shrink to its row. */
  max-width: 100%;
  min-width: 0;
  font: inherit;
  font-size: var(--t-min);
  padding: 3px 6px;
  border: 1px solid var(--line);
  border-radius: 5px;
  background: var(--panel);
  color: var(--ink);
}

.ownerAction {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: var(--t-min);
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}

.ownerAction:disabled {
  color: var(--muted);
  cursor: default;
  text-decoration: none;
}

/* **Its own line.** The server's refusal names the fact to fix — "that person is not in this
   organisation" — and as a flex item beside a label and a select that sentence renders a word at a
   time. The same reasoning as `.declareError`, in the other layout. */
.ownerError {
  flex-basis: 100%;
  margin: 2px 0 0;
}

/* ---------------------------------------------------------------------------------------------
   Who bills what (ADR-0062), and the nil that can be taken back (ADR-0064).
   --------------------------------------------------------------------------------------------- */

/* The meters nobody has accounted for. A list under its own heading rather than a count in a
   sentence: a screen that listed accounts and left these off would read as complete while most of
   the meters were not on it. Set apart by a rule, not a colour — it is "not recorded", which is a
   fact about what somebody has said, and is not a fault. */
.registerList {
  margin: 14px 0 0;
  padding: 10px 12px;
  border-left: 2px solid var(--line-2);
  background: var(--panel-2);
}

/* A checkbox list that scrolls inside itself: forty meters on one contract is the case this
   exists for, and a screen that grew by forty rows would push the next panel out of reach. */
.pick {
  display: grid;
  gap: 4px;
  max-height: 220px;
  margin: 0;
  padding: 6px 8px;
  overflow-y: auto;
  border: 1px solid var(--line);
  border-radius: 5px;
  background: var(--panel);
}

.pickItem {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink);
}

/* Who billed this stream, on the row where its gaps are read. A line of its own beside the owner's:
   the person reading a gap is the person who rings the vendor. Plain ink — it is information. */
.supply {
  min-width: 0;
  margin: 0;
  font-size: var(--t-min);
  line-height: 1.4;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}

/* *Not recorded* is said in `--warn`'s sibling register: a thing to resolve and not a fault, the
   reading `.ownerNobody` gets for the same reason. The word carries the meaning; the weight only
   keeps it findable. */
.supplyNotRecorded {
  font-weight: 600;
  color: var(--ink-2);
}

/* The trail of declarations behind one source: who said nil, who took it back, and why. */
.trail {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 6px;
}

.trailItem {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* A withdrawn declaration is kept whole and struck through nothing: it is history, and the words
   "withdrawn" and "standing" say which is which — never the styling alone. */
.trailWithdrawn {
  color: var(--muted);
}

/* A nil cell a person can take back. Pointer and hover, as `.actionable` — a separate class so the
   gap cells' behaviour is not changed by a neighbour's. */
.withdrawable {
  cursor: pointer;
}

.withdrawable:hover {
  filter: brightness(1.1);
}

/* The twelve months of a season, as checkboxes that wrap: a choice of months is twelve small
   decisions, and a multi-select list would hide which are chosen. */
.monthRow {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
}

.monthPick {
  font-size: var(--t-min);
  color: var(--ink);
}

/* The glyph inside a census cell: the colour of the word beside it, never a colour of its own. */
.glyph {
  flex-shrink: 0;
}

/* Accounts: the vendor and its reference, how many meters sit under it, and the two acts an
   Approver has on it. Same rule as the stream register: the first cells are one line, and
   everything after them takes the width beneath. */
.accountCols {
  --cols: minmax(0, 2fr) minmax(0, 1fr) auto;
}

.accountRow > :not(.rowHead, .owner, :nth-child(2)) {
  grid-column: 1 / -1;
}

.accountRow > .owner {
  grid-column: 3;
  grid-row: 1;
  justify-content: flex-end;
}

/* Forty meters on one contract is the case: columns, so the list is a block and not a ladder. */
.meterList {
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 2px 18px;
}

/* The unrecorded list under the table keeps its own padding, and runs to the panel's edge. */
.registerFlush {
  margin: 0;
  border-top: 1px solid var(--line);
  border-left: 0;
}
